import type {DragEvent, ReactElement} from 'react';
import {useCallback, useContext, useEffect, useRef, useState} from 'react';
import type {Component} from 'grapesjs';
import {Icon} from '@Components/icon-v2/icon';
import {IconSize} from '@Components/icon-v2/icon.types';
import {Text, TextSize} from '@Components/text';
import {emailMakerContext} from '@EmailMakerReact/email-maker-react.context';
import {EMAIL_MAKER_COMPONENT_TYPES} from '@EmailMakerReact/email-maker-react.constants';
import {addComponentAnchor} from '@EmailMakerReact/lib/email-maker-anchor-link-library';
import {getComponentStyleProperty, setComponentStyle} from '@EmailMakerReact/lib/email-maker-component-style-library';
import {updateSocialLinkSpacing, updateSocialLinkSize} from '@EmailMakerReact/lib/email-maker-social-signup-library';
import {getStringAttributes, getStringStyles} from '@EmailMakerReact/lib/email-maker-component-query-library';
import useOutsideClick from '@Hooks/useOutsideClick';
import type {EmailMakerControlProps} from '../../right-panel-registry.types';
import {DEFAULT_SOCIAL_IDS, SOCIAL_ICONS_DIRECTORY_NAME, SOCIAL_LINKS_CACHE_KEY, stripHttpsPrefix} from './social-links-control.types';
import type {SocialLinkPlatformData, SocialLinksAPIResponse, SocialLinkRowData} from './social-links-control.types';
import {SocialLinkItem} from './components/social-link-item/social-link-item';
import styles from './social-links-control.module.scss';

function getIconStyle(component: Component): string {
  const style = getComponentStyleProperty(component, 'icon-style');
  return style !== '' ? style : 'original';
}

function hasInconsistentIconStyle(logoURL: string, iconStyle: string): boolean {
  const parts = logoURL.split(SOCIAL_ICONS_DIRECTORY_NAME);
  if (parts.length <= 1) {
    return false;
  }
  const iconDirectory = parts[1].split('/')[0];
  return iconDirectory !== iconStyle;
}

function findImageByPlatformName(images: Component[], platformName: string): Component | undefined {
  return images.find((img: Component) => {
    const attrs = getStringAttributes(img);
    return attrs['data-platform-name'] === platformName;
  });
}

function readRowsFromComponent(component: Component): SocialLinkRowData[] {
  const imageComponents = component.findType(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_IMAGE);
  return imageComponents.map((imageComp: Component): SocialLinkRowData => {
    const attrs = getStringAttributes(imageComp);
    return {
      id: attrs['data-id'] ?? '',
      logoURL: attrs['data-logo-url'] ?? '',
      baseURL: attrs['data-base-url'] ?? '',
      platformURL: attrs['data-platform-url'] ?? '',
      platformName: attrs['data-platform-name'] ?? '',
      imageComponent: imageComp,
    };
  });
}

export function SocialLinksControl({component}: EmailMakerControlProps): ReactElement {
  const {editor} = useContext(emailMakerContext);
  const [rows, setRows] = useState<SocialLinkRowData[]>(() => readRowsFromComponent(component));
  const [platforms, setPlatforms] = useState<SocialLinkPlatformData[]>([]);
  const [isDialogOpen, setIsDialogOpen] = useState<boolean>(false);
  const [isLoadingPlatforms, setIsLoadingPlatforms] = useState<boolean>(false);
  const dragItemId = useRef<string | null>(null);
  const dialogWrapperRef = useRef<HTMLDivElement | null>(null);

  const iconStyle = getIconStyle(component);

  /** Re-reads rows from GrapesJS, which suppresses change:components for internal appends/removals. */
  const syncRowsFromComponent = useCallback((): void => {
    setRows(readRowsFromComponent(component));
  }, [component]);

  // Control is reused without remounting; re-seed rows on component change and keep them live.
  useEffect(() => {
    syncRowsFromComponent();
    component.on('change:components', syncRowsFromComponent);
    return (): void => {
      component.off('change:components', syncRowsFromComponent);
    };
  }, [component, syncRowsFromComponent]);

  // Icon-style changes rewrite each social image's src/data-logo-url (change:attributes), which the
  // change:components listener above doesn't catch; re-sync rows so the list reflects the new style.
  useEffect(() => {
    syncRowsFromComponent();
  }, [iconStyle, syncRowsFromComponent]);

  useEffect(() => {
    const cached = window.PMW.getCacheItem(SOCIAL_LINKS_CACHE_KEY) as SocialLinkPlatformData[] | null;
    if (cached) {
      setPlatforms(cached);
      return;
    }

    let isCancelled = false;

    setIsLoadingPlatforms(true);
    void (window.PMW.readLocal('emailmaker/getSocialLinksURLs', {}) as Promise<SocialLinksAPIResponse>)
      .then((r: SocialLinksAPIResponse) => {
        if (isCancelled) {
          return;
        }
        if (Array.isArray(r.socialLinks)) {
          window.PMW.setCacheItem(SOCIAL_LINKS_CACHE_KEY, r.socialLinks);
          setPlatforms(r.socialLinks);
        }
      })
      .finally(() => {
        if (!isCancelled) {
          setIsLoadingPlatforms(false);
        }
      });

    return (): void => {
      isCancelled = true;
    };
  }, []);

  useEffect(() => {
    if (platforms.length === 0) {
      return;
    }

    const hasItems = component.findType(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_ITEM).length > 0;
    if (hasItems) {
      return;
    }

    DEFAULT_SOCIAL_IDS.forEach((id: string) => {
      const platform = platforms.find((p: SocialLinkPlatformData) => p.id === id);
      if (platform) {
        addSocialLinkComponent(platform, '');
      }
    });

    syncRowsFromComponent();
  }, [platforms, syncRowsFromComponent]);

  function addSocialLinkComponent(platform: SocialLinkPlatformData, baseURL: string): void {
    if (!editor) {
      return;
    }

    const existingImages = component.findType(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_IMAGE);
    const existingImage = findImageByPlatformName(existingImages, platform.name);

    // Read fresh from the component so newly added links match the current icon style
    // regardless of how addSocialLinkComponent is memoized.
    let displayIconStyle = getIconStyle(component);
    if (displayIconStyle === 'none') {
      displayIconStyle = 'original';
    }

    const logoURL = platform.logos[displayIconStyle] ?? platform.logos.original ?? '';
    const platformURL = stripHttpsPrefix(platform.baseURL);

    const newCompData = {
      type: EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_IMAGE,
      stylable: true,
      removable: false,
      hoverable: false,
      selectable: false,
      draggable: false,
      highlightable: false,
      attributes: {
        src: logoURL,
        alt: platform.name,
        'data-platform-name': platform.name,
        'data-id': platform.id,
        'data-logo-url': logoURL,
        'data-base-url': existingImage ? (getStringAttributes(existingImage)['data-base-url'] ?? baseURL) : baseURL,
        'data-gjs-selectable': 'false',
        'data-platform-url': platformURL,
        'data-gjs-hoverable': 'false',
      },
    };

    let socialPlatformImage: Component;
    if (existingImage) {
      const replaced = existingImage.replaceWith(newCompData);
      socialPlatformImage = Array.isArray(replaced) ? replaced[0] : replaced;
      if (!socialPlatformImage) {
        return;
      }
    } else {
      socialPlatformImage = component.append(newCompData)[0];
    }

    addComponentAnchor(editor, {
      component: socialPlatformImage,
      url: baseURL,
      customLinkComponent: EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_ITEM,
    });

    applyExistingSpacingAndSize();
  }

  function applyExistingSpacingAndSize(): void {
    if (!editor) {
      return;
    }
    const style = getStringStyles(component);
    if (style['pmw-icon-margin']) {
      updateSocialLinkSpacing(editor, parseInt(style['pmw-icon-margin'], 10));
    }
    if (style['pmw-icon-size']) {
      updateSocialLinkSize(editor, parseInt(style['pmw-icon-size'], 10));
    }
  }

  const handleAddPlatform = useCallback(
    (platform: SocialLinkPlatformData): void => {
      const alreadyAdded = rows.some((r: SocialLinkRowData) => r.id === platform.id);
      if (alreadyAdded) {
        return;
      }
      addSocialLinkComponent(platform, '');
      syncRowsFromComponent();
      setIsDialogOpen(false);
    },
    [rows, syncRowsFromComponent]
  );

  const handleRemove = useCallback(
    (id: string): void => {
      if (!editor) {
        return;
      }
      const imageComponents = component.findType(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_IMAGE);
      imageComponents.forEach((imageComp: Component) => {
        const attrs = getStringAttributes(imageComp);
        if (attrs['data-id'] === id) {
          imageComp.parent()?.remove();
        }
      });
      syncRowsFromComponent();
    },
    [editor, component, syncRowsFromComponent]
  );

  const handleUrlCommit = useCallback(
    (id: string, url: string): void => {
      const imageComponents = component.findType(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_IMAGE);
      imageComponents.forEach((imageComp: Component) => {
        const attrs = getStringAttributes(imageComp);
        if (attrs['data-id'] !== id) {
          return;
        }
        const currentBaseUrl = attrs['data-base-url'] ?? '';
        if (currentBaseUrl === url) {
          return;
        }
        imageComp.addAttributes({'data-base-url': url});
        const parent = imageComp.parent();
        parent?.addAttributes({
          href: url,
          'data-url': url,
          'data-base-url': url,
        });
      });
    },
    [component]
  );

  const handleDragStart = useCallback((_e: DragEvent<HTMLDivElement>, id: string): void => {
    dragItemId.current = id;
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>): void => {
    e.preventDefault();
  }, []);

  // GrapesJS fires the remove half of a move with {temporary: true}; re-read rows directly after drop.
  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, targetId: string): void => {
      e.preventDefault();
      const sourceId = dragItemId.current;
      if (!sourceId || sourceId === targetId) {
        dragItemId.current = null;
        return;
      }

      const currentRows = readRowsFromComponent(component);
      const oldIndex = currentRows.findIndex((r: SocialLinkRowData) => r.id === sourceId);
      const newIndex = currentRows.findIndex((r: SocialLinkRowData) => r.id === targetId);

      if (oldIndex === -1 || newIndex === -1) {
        dragItemId.current = null;
        return;
      }

      const socialItems = component.findType(EMAIL_MAKER_COMPONENT_TYPES.social.PMW_SOCIALS_ITEM);
      if (oldIndex < socialItems.length) {
        const itemToMove = socialItems[oldIndex];
        const parent = itemToMove.parent();
        itemToMove.remove({temporary: true});
        parent?.append(itemToMove, {at: newIndex});
      }

      dragItemId.current = null;
      syncRowsFromComponent();
    },
    [component, syncRowsFromComponent]
  );

  const toggleDialog = useCallback((): void => {
    setIsDialogOpen((prev: boolean) => !prev);
  }, []);

  const closeDialog = useCallback((): void => {
    setIsDialogOpen(false);
  }, []);

  useOutsideClick(dialogWrapperRef, closeDialog, null, () => isDialogOpen);

  // Effect keeps model writes out of rows.map(); reads fresh rows because setAttributes fires change:attributes, not change:components.
  useEffect(() => {
    if (!editor) {
      return;
    }
    const currentRows = readRowsFromComponent(component);
    const hasInconsistency = currentRows.some((row: SocialLinkRowData) => hasInconsistentIconStyle(row.logoURL, iconStyle));
    if (hasInconsistency) {
      setComponentStyle(editor, {component, property: 'icon-style', value: 'none'});
    }
  }, [rows, iconStyle, editor, component]);

  function resolveLogoUrl(row: SocialLinkRowData): string {
    if (hasInconsistentIconStyle(row.logoURL, iconStyle)) {
      const platform = platforms.find((p: SocialLinkPlatformData) => p.id === row.id);
      if (platform) {
        return platform.logos.original ?? row.logoURL;
      }
    }
    return row.logoURL;
  }

  function renderLoadingRow(): ReactElement | null {
    if (!isLoadingPlatforms) {
      return null;
    }
    return (
      <li className={styles.loadingItem}>
        <Text val={window.i18next.t('pmwjs_loading')} size={TextSize.SM} className="content-body" />
      </li>
    );
  }

  function renderPlatformDialog(): ReactElement | null {
    if (!isDialogOpen) {
      return null;
    }

    return (
      <ul className={`social-link-items-list bg-neutral-0 radius-8 spacing-p-3 border-s-standard ${styles.platformList}`}>
        {renderLoadingRow()}
        {platforms.map((platform: SocialLinkPlatformData) => {
          const isAdded = rows.some((r: SocialLinkRowData) => r.id === platform.id);
          const displayIconStyle = iconStyle === 'none' ? 'original' : iconStyle;
          const logoUrl = platform.logos[displayIconStyle] ?? platform.logos.original ?? '';
          const placeholderURL = stripHttpsPrefix(platform.baseURL);

          return (
            <li
              aria-disabled={isAdded}
              className={`social-link-item spacing-m-b-2 radius-8 bg-neutral-0 flexbox flex-content-start flex-items-start ${styles.platformItem} ${isAdded ? styles.platformItemDisabled : ''}`}
              data-id={platform.id}
              key={platform.id}
              onClick={
                isAdded
                  ? undefined
                  : (): void => {
                      handleAddPlatform(platform);
                    }
              }
              title={isAdded ? window.i18next.t('pmwjs_social_link_already_added') : placeholderURL}
            >
              <img alt={platform.name} className={`social-link-img spacing-m-r-2 ${styles.platformListImg}`} src={logoUrl} />
              <Text val={platform.name} size={TextSize.MD} className={`social-link-text content-body ${styles.platformText}`} />
            </li>
          );
        })}
      </ul>
    );
  }

  return (
    <div className={`js-social-links-box social-links-box ${styles.container}`}>
      <div className={`js-sortable-social-links js-socials-container socials-container ${styles.linksContainer}`}>
        {rows.map((row: SocialLinkRowData) => (
          <SocialLinkItem
            iconStyle={iconStyle}
            key={row.id}
            onDragOver={handleDragOver}
            onDragStart={handleDragStart}
            onDrop={handleDrop}
            onRemove={handleRemove}
            onUrlCommit={handleUrlCommit}
            row={{...row, logoURL: resolveLogoUrl(row)}}
          />
        ))}
      </div>
      <div className={styles.dialogWrapper} ref={dialogWrapperRef}>
        <button
          aria-label={window.i18next.t('pmwjs_social_links')}
          className={`js-add-social-link ${styles.addButton} ${isDialogOpen ? '-open' : ''}`}
          onClick={toggleDialog}
          type="button"
        >
          <Icon aria-hidden className="content-body" icon="icon-plus" size={IconSize.SIZE_ICON_16} />
        </button>
        <div className={`links-dialog-container js-links-dialog-container ${styles.dialogContainer} ${isDialogOpen ? '-open' : ''}`}>{renderPlatformDialog()}</div>
      </div>
    </div>
  );
}
